// @ts-nocheck
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import lockFill from '@iconify/icons-eva/lock-fill';
import unlockOutline from '@iconify/icons-eva/unlock-outline';
// material
import { alpha, styled } from '@mui/material/styles';
import { Box, Link, Stack, Button, Drawer, Tooltip, Typography, CardActionArea } from '@mui/material';
// hooks
import useAuth from '../../hooks/useAuth';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
// routes
import { PATH_DASHBOARD, PATH_DOCS } from '../../routes/paths';
// components
import Logo from '../../components/Logo';
import MyAvatar from '../../components/MyAvatar';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import { MHidden } from '../../components/@material-extend';
//
import sidebarConfig from './SidebarConfig';
import { BANNER_HEIGHT } from '../../components/ImpersonationBanner';
import { DocIllustration } from '../../assets';
import { menuConfig, groupNav } from '../../constants/defaultValues';
import useWorkplaceTerm from '../../hooks/useWorkplaceTerm';
import useRoleName from '../../hooks/useRoleName';
// ----------------------------------------------------------------------

// Navigation labels that belong to the organisation's industry rather than to
// construction. A cafe's sidebar should read Venues, not Job Sites.
//
// The menu config keeps its original `title`, because that string is also the
// item's IDENTITY — the pre-onboarding filter below hides items by matching it,
// as does the campaigns gate. Renaming there would silently break both. So the
// stored title stays put and only the rendered label is swapped, after
// filtering, through this one table.
//
// Keeping the lookup here, next to the filter that matches the same strings,
// means both couplings to those literals live in one file instead of drifting
// apart across the app.
const WORKPLACE_TITLES = {
  'Job Sites': (t) => t.nounPlural,
  'Job Site SWMS': (t) => `${t.noun} SWMS`,
};

// Two ways a title can change, and they answer different questions.
//
// WORKPLACE_TITLES above is a RULE: the item is named after the workplace, so
// the noun substitutes into it and every industry gets the right answer for
// free. navLabels is VOCABULARY: "On site now" cannot be built from "Venue",
// because the preposition moves with the industry, so the client stores the
// phrase per industry instead.
//
// The stored phrase wins where both could apply. It is the more specific
// answer, and it is the one a human chose.
function applyWorkplaceTitles(items, terms) {
  return (items || []).map((item) => {
    // Recurses, because grouping runs before this and a renamed item can now be
    // sitting inside a group. Non-recursive, "Job Site SWMS" would keep its
    // construction name the moment it moved under a parent.
    const children = item?.children ? applyWorkplaceTitles(item.children, terms) : undefined;
    const withChildren = children ? { ...item, children } : item;
    const stored = terms?.navLabels?.[item?.title];
    if (stored) return { ...withChildren, title: stored };
    const rename = WORKPLACE_TITLES[item?.title];
    if (!rename) return withChildren;
    return { ...withChildren, title: rename(terms) };
  });
}

const mystyle = { textDecoration:"none" };
const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeOut
    })
  }
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12]
}));

// ----------------------------------------------------------------------

IconCollapse.propTypes = {
  onToggleCollapse: PropTypes.func,
  collapseClick: PropTypes.bool
};

function IconCollapse({ onToggleCollapse, collapseClick }) {
  return (
    <Tooltip title={collapseClick ? 'Unlock menu' : 'Lock menu mini'}>
      <CardActionArea
        onClick={onToggleCollapse}
        sx={{
          width: 24,
          height: 24,
          display: 'flex',
          cursor: 'pointer',
          borderRadius: '50%',
          alignItems: 'center',
          color: collapseClick ? 'primary.main' : 'text.primary',
          justifyContent: 'center',
          transition: (theme) => theme.transitions.create(['color', 'background-color']),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
          }
        }}
      >
        <Icon icon={collapseClick ? lockFill : unlockOutline} width={16} height={16} />
      </CardActionArea>
    </Tooltip>
  );
}

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const { user, impersonation } = useAuth();
  const workplaceTerms = useWorkplaceTerm();
  const roleName = useRoleName();
  const bannerOffset = impersonation.isImpersonating ? BANNER_HEIGHT : 0;
  const [menu, setMenu] = useState(null)
  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  useEffect(()=>{
    const base = menuConfig[user.role];
    if (!base) { setMenu(null); return; }
    // Hide nav items the backend would reject so we don't show broken links.
    const hidden = new Set();
    // Before onboarding is finished there is no organisation to scope anything
    // to, and the backend says so — requireOnboardingComplete guards the
    // dashboard summary, jobsites and team, and OnboardingGuard guards every
    // module route. Listing a module here keeps the sidebar honest instead of
    // offering links that bounce straight back to the checklist.
    //
    // Matched on the STORED title, which is why these read as Brendon's module
    // names: the rename below happens after this filter, never before it.
    if (!user.onboardingCompleted) {
      [
        'Overview',
        'Job Sites',
        'Contractor',
        'Check in',
        'Incidents',
        'Audits & Inspections',
        'Risk Register',
        'People and Training',
        'Noticeboard',
        'Chemical',
        'Safety Planning',
        'Tasks',
        'Action / Hazards',
        // Behind OnboardingGuard like the rest, so listing it here keeps the
        // sidebar from offering a link that bounces back to the checklist.
        'Subscription',
      ].forEach((t) => hidden.add(t));
    }
    // Campaigns is gated to an allow-list of builders (BE: /v1/auth/me
    // campaignsEnabled mirrors CAMPAIGN_ENABLED_BUILDER_IDS).
    if (!user.campaignsEnabled) hidden.add('Communication');
    // While a company works for clients, THEIR requirements govern its team and
    // its own grid is dormant. Showing an editable grid then would be showing a
    // control that does nothing — the owner marks a document optional, reads
    // that as "my people don't need it", and the client's rules quietly decide
    // otherwise. My companies is the answer to "what must we hold" instead.
    if (user.requirementsGovernedByClients) hidden.add('Requirements');
    // Modules that only make sense on a building site. The industry supplies
    // the list, so a cafe stops being shown Pool Types without anything here
    // needing to know what a cafe is.
    for (const title of workplaceTerms.hiddenModules) hidden.add(title);
    // Filter on the stored titles first — they are the identity the gates above
    // match on — and only then swap the labels the industry renames.
    //
    // Children are filtered too: some gated entries (Requirements) live under a
    // parent, and a top-level-only filter left them on screen. A parent whose
    // children all disappear keeps its own link, which is correct — Documents
    // is a real destination in its own right.
    const keep = (it) => !hidden.has(it?.title);
    const visible = hidden.size
      ? (base.items || [])
          .filter(keep)
          .map((it) => (it?.children ? { ...it, children: it.children.filter(keep) } : it))
      : (base.items || []);
    // The section heading is the role, and it was the last place in the
    // navigation still reading "Builder" to a venue operator. Same source as
    // the account block below it, so the two cannot disagree.
    setMenu({
      ...base,
      subheader: roleName(user.role) || base.subheader,
      // Filter, then group, then rename — see groupNav. The gates above match
      // stored top-level titles, so grouping has to come after them.
      items: applyWorkplaceTitles(groupNav(visible), workplaceTerms),
    });
    // `workplaceTerms` whole, not field by field. Picking fields out was an
    // attempt to avoid depending on an object rebuilt every render — but
    // `hiddenModules` and `navLabels` ARE objects, so the effect re-fired on
    // every render, called setMenu, and looped until React bailed out with
    // "Maximum update depth exceeded". Construction hit it hardest: both fields
    // are NULL for it, so the hook produced a fresh [] and {} each time.
    //
    // useWorkplaceTerm is memoised now, so the whole object is a stable
    // reference and depending on it is both correct and honest.
  }, [
    user?.role,
    user?.onboardingCompleted,
    user?.campaignsEnabled,
    workplaceTerms,
    roleName,
  ])
  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Stack
        spacing={3}
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: 'center'
          })
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box component={RouterLink} to="/" sx={{ display: 'inline-flex' }} style={{ textDecoration:"none" }}>
            <Logo showText={!isCollapse} />
          </Box>

          <MHidden width="lgDown">
            {!isCollapse && <IconCollapse onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />}
          </MHidden>
        </Stack>

        {isCollapse ? (
          <MyAvatar sx={{ mx: 'auto', mb: 2 }} />
        ) : (
          <Link underline="none" component={RouterLink} to={PATH_DASHBOARD.user.profile} >
            <AccountStyle>
              <MyAvatar />
              <Box sx={{ ml: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                  {user.first_name+' '+user.last_name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {roleName(user.role)}
                </Typography>
              </Box>
            </AccountStyle>
          </Link>
        )}
      </Stack>
      {
        menu?(
          <NavSection menu={menu} navConfig={sidebarConfig} isShow={!isCollapse} />
        ):null
      }
      

      <Box sx={{ flexGrow: 1 }} />

      {/* {!isCollapse && (
        <Stack spacing={3} alignItems="center" sx={{ px: 5, pb: 5, mt: 10, width: 1, textAlign: 'center' }}>
          <DocIllustration sx={{ width: 1 }} />

          <div>
            <Typography gutterBottom variant="subtitle1">
              Hi, {user?.displayName}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Need help?
              <br /> Please check our docs
            </Typography>
          </div>
          <Button href={PATH_DOCS} target="_blank" variant="contained">
            Documentation
          </Button>
        </Stack>
      )} */}
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH
        },
        ...(collapseClick && {
          position: 'absolute'
        })
      }}
    >
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH, top: bannerOffset, height: `calc(100% - ${bannerOffset}px)` }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default',
              overflowX: 'hidden',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.shorter,
                  easing: theme.transitions.easing.easeOut
                }),
              '& .MuiListItemText-root, & .MuiListItemText-root > *': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              },
              ...(isCollapse && {
                width: COLLAPSE_WIDTH
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.88)
              }),
              ...(bannerOffset && {
                top: bannerOffset,
                height: `calc(100% - ${bannerOffset}px)`
              })
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}